<template>
  <div class="flex min-h-0 flex-1 flex-col gap-3">
    <!-- Skeleton: the accounting software is called only when this tab is opened,
         so the first paint has nothing to show yet. -->
    <div v-if="loading" class="flex flex-col gap-2" aria-busy="true">
      <div
        v-for="row in 4"
        :key="row"
        class="flex animate-pulse flex-row items-center gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-600"
      >
        <div
          class="h-8 w-8 shrink-0 rounded-full bg-slate-200 dark:bg-slate-600"
        />
        <div class="flex flex-1 flex-col gap-2">
          <div class="h-3 w-1/3 rounded bg-slate-200 dark:bg-slate-600" />
          <div class="h-3 w-1/2 rounded bg-slate-100 dark:bg-slate-700" />
        </div>
        <div class="h-4 w-20 rounded bg-slate-200 dark:bg-slate-600" />
      </div>
    </div>

    <div
      v-else-if="!comparison?.available"
      class="flex flex-1 flex-col items-center justify-center gap-2 text-center text-slate-400"
    >
      <FontAwesomeIcon :icon="['fas', 'link-slash']" size="2x" />
      <p class="capitalize-first">{{ unavailableMessage }}</p>
    </div>

    <template v-else>
      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <!--
          Billit reports the invoice as a whole (total, outstanding, status), not
          its payments one by one: those figures are what the sync reads.
        -->
        <section
          v-if="isBillit"
          class="flex flex-col gap-1 rounded-md border border-slate-200 p-3 text-sm dark:border-slate-600"
          data-test="partner-summary"
        >
          <div class="flex flex-row justify-between gap-2">
            <span class="text-slate-500 capitalize-first">
              {{
                $t('invoicing.chift_tab_partner_total', {
                  partner: partnerName
                })
              }}
            </span>
            <span class="font-semibold">
              {{ formatOptionalCurrency(comparison.partner_total) }}
            </span>
          </div>
          <div class="flex flex-row justify-between gap-2">
            <span class="text-slate-500 capitalize-first">
              {{ $t('invoicing.chift_tab_outstanding') }}
            </span>
            <span class="font-semibold">
              {{ formatOptionalCurrency(comparison.outstanding) }}
            </span>
          </div>
          <div
            v-if="comparison.partner_status"
            class="flex flex-row justify-between gap-2"
          >
            <span class="text-slate-500 capitalize-first">
              {{
                $t('invoicing.chift_tab_partner_status', {
                  partner: partnerName
                })
              }}
            </span>
            <span class="font-semibold">{{ partnerStatusLabel }}</span>
          </div>
          <span
            v-if="comparison.total_mismatch"
            class="mt-1 self-start rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700 capitalize-first"
            data-test="total-mismatch"
          >
            {{
              $t('invoicing.payments_sync_total_mismatch', {
                partner: partnerName
              })
            }}
          </span>
        </section>

        <section v-if="accountingPayments.length" class="flex flex-col gap-2">
          <h4 class="text-sm font-semibold text-slate-500 capitalize-first">
            {{ accountingSideTitle }}
          </h4>

          <div
            v-for="payment in accountingPayments"
            :key="payment.chift_id ?? payment.id"
            class="flex flex-row items-center gap-3 rounded-md border p-3"
            :class="rowClass(payment.status)"
          >
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              :class="badgeClass(payment.status)"
              :title="statusLabel(payment.status)"
            >
              <FontAwesomeIcon :icon="['fas', statusIcon(payment.status)]" />
            </div>

            <div class="flex min-w-0 flex-1 flex-col">
              <div class="flex flex-row items-baseline gap-2">
                <span class="font-medium">
                  {{ payment.journal_name ?? partnerName }}
                </span>
                <span
                  v-if="payment.payment_date"
                  class="text-xs text-slate-400"
                >
                  {{ $h.formatDate(payment.payment_date, 'DD/MM/YYYY') }}
                </span>
              </div>
              <span class="truncate text-xs text-slate-500">
                {{ statusLabel(payment.status) }}
                <template v-if="payment.matching_number">
                  · {{ payment.matching_number }}
                </template>
                <template v-if="payment.communication">
                  · {{ payment.communication }}
                </template>
              </span>
              <!--
                Purely informative: the flag decides nothing, the sync books on the
                dedicated amount and the journal type. Shown so a settlement the
                accounting software has not finished reconciling stays visible to
                whoever is investigating, instead of being silently indistinguishable.
              -->
              <span
                v-if="payment.reconciled === false"
                class="truncate text-xs text-slate-400 italic"
                :title="$t('invoicing.chift_tab_not_reconciled_hint')"
              >
                {{ $t('invoicing.chift_tab_not_reconciled') }}
              </span>
            </div>

            <div class="flex shrink-0 flex-col items-end">
              <span class="font-semibold">
                {{ $h.formatCurrency(payment.dedicated_amount, currency) }}
              </span>
              <!-- A payment settling several invoices is reported at its full amount
                   on each one; showing both avoids reading it as this invoice's. -->
              <span
                v-if="showsFullAmount(payment)"
                class="text-xs text-slate-400"
                :title="$t('invoicing.chift_tab_full_amount_hint')"
              >
                {{ $t('invoicing.chift_tab_payment_total') }}
                {{ $h.formatCurrency(payment.amount, currency) }}
              </span>
            </div>
          </div>
        </section>

        <section v-if="manualPayments.length" class="flex flex-col gap-2">
          <h4 class="text-sm font-semibold text-slate-500 capitalize-first">
            {{ $t('invoicing.chift_tab_fm_only') }}
          </h4>

          <div
            v-for="payment in manualPayments"
            :key="payment.id"
            class="flex flex-row items-center gap-3 rounded-md border border-slate-200 p-3 dark:border-slate-600"
          >
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-600 dark:text-slate-200"
            >
              <FontAwesomeIcon :icon="['fas', 'pen']" />
            </div>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="font-medium">
                {{ $t('invoicing.chift_tab_manual_entry') }}
              </span>
              <span class="text-xs text-slate-400">
                {{ $h.formatDate(payment.payment_date, 'DD/MM/YYYY') }}
              </span>
            </div>
            <span class="shrink-0 font-semibold">
              {{ $h.formatCurrency(payment.amount, currency) }}
            </span>
          </div>
        </section>

        <div
          v-if="!accountingPayments.length && !manualPayments.length"
          class="flex flex-1 flex-row items-center justify-center text-slate-300"
        >
          {{ $t('invoicing.chift_tab_empty') }}
        </div>

        <button
          v-if="invoiceId"
          type="button"
          class="mt-2 flex items-center gap-2 self-start text-xs text-slate-500 underline hover:text-slate-700"
          @click="showHistory = true"
        >
          <FontAwesomeIcon :icon="['fal', 'clock-rotate-left']" />
          {{ $t('invoicing.payment_sync_invoice_history') }}
        </button>
      </div>
    </template>

    <ChiftPaymentSyncRunModal
      v-if="showHistory"
      :invoice-id="invoiceId"
      @close="showHistory = false"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ChiftPaymentSyncRunModal from '@c/Partner/Chift/ChiftPaymentSyncRunModal.vue'
import { paymentSyncPartnerName } from '@c/Partner/Chift/paymentSyncPartner'

const KNOWN_PARTNER_STATUSES = ['posted', 'paid', 'draft', 'cancelled']

export default {
  name: 'PaymentChiftComparisonPanel',
  components: { FontAwesomeIcon, ChiftPaymentSyncRunModal },
  props: {
    comparison: {
      type: Object,
      default: null
    },
    invoiceId: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      default: 'EUR'
    }
  },
  data() {
    return {
      showHistory: false
    }
  },
  computed: {
    accountingPayments() {
      return this.comparison?.accounting_payments ?? []
    },
    manualPayments() {
      return this.comparison?.manual_payments ?? []
    },
    isBillit() {
      return this.comparison?.partner === 'billit'
    },
    partnerName() {
      return paymentSyncPartnerName(this.comparison?.partner)
    },
    accountingSideTitle() {
      return this.isBillit
        ? this.$t('invoicing.chift_tab_partner_side', {
            partner: this.partnerName
          })
        : this.$t('invoicing.chift_tab_accounting_side')
    },
    /**
     * Billit's own status, translated when known; an unknown one is shown as
     * Billit sent it rather than hidden.
     */
    partnerStatusLabel() {
      const status = this.comparison?.partner_status

      return KNOWN_PARTNER_STATUSES.includes(status)
        ? this.$t(`invoicing.chift_partner_status_${status}`)
        : status
    },
    unavailableMessage() {
      return this.$t(
        `invoicing.chift_tab_unavailable_${this.comparison?.reason ?? 'unreachable'}`
      )
    }
  },
  methods: {
    statusIcon(status) {
      return {
        synced: 'check',
        missing: 'clock',
        no_money_moved: 'circle-info'
      }[status]
    },
    statusLabel(status) {
      return this.$t(`invoicing.chift_tab_status_${status}`)
    },
    badgeClass(status) {
      return {
        synced: 'bg-green-100 text-green-700',
        missing: 'bg-amber-100 text-amber-700',
        no_money_moved: 'bg-slate-100 text-slate-500'
      }[status]
    },
    rowClass(status) {
      return {
        synced: 'border-green-200',
        missing: 'border-amber-300 bg-amber-50/40',
        no_money_moved: 'border-slate-200'
      }[status]
    },
    formatOptionalCurrency(amount) {
      return amount === null || amount === undefined
        ? '—'
        : this.$h.formatCurrency(amount, this.currency)
    },
    showsFullAmount(payment) {
      return Math.abs(payment.amount - payment.dedicated_amount) > 0.005
    }
  }
}
</script>
